// components/chat/ChatModals.jsx
// ─────────────────────────────────────────────────────────────
// Thin wrappers around the real HRMS module modals.
// ChatScreen renders these when the AI triggers an action.
// On successful submission each modal calls onSuccess(message)
// which posts a confirmation back into the chat thread.
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";

import LeaveModals         from "../leave/LeaveModals";
import RegularisationModal from "../attendance/RegularisationModal";
import TicketSubmissionForm from "../tickets/TicketSubmissionForm";
import CorrectionModal     from "../payroll/CorrectionModal";
import SelfReviewForm      from "../performance/SelfReviewForm";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON     = "#00FF99";

// ─────────────────────────────────────────────────────────────
// LEAVE APPLY MODAL
// ─────────────────────────────────────────────────────────────
export function ChatLeaveModal({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    leave_type: "", start_date: "", end_date: "", reason: "", file: null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((p) => ({ ...p, [name]: files ? files[0] : value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("leave_type",  formData.leave_type);
      fd.append("start_date",  formData.start_date);
      fd.append("end_date",    formData.end_date);
      fd.append("reason",      formData.reason || "");
      if (formData.file) fd.append("file", formData.file);

      const res = await fetch(`${API_BASE}/api/leave/submit`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body:    fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Submission failed");
      }

      onSuccess?.(
        `✅ Your ${formData.leave_type} leave request from ${formData.start_date} to ${formData.end_date} has been submitted and is pending manager approval.`
      );
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <LeaveModals
        showForm={open}
        formData={formData}
        setShowForm={(v) => { if (!v) onClose?.(); }}
        handleFormChange={handleFormChange}
        handleFormSubmit={handleFormSubmit}
      />
      {/* Error and loading overlaid inside the modal */}
      {error && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[10000] bg-red-900/80 border border-red-500 text-red-200 text-sm px-4 py-2 rounded-lg">
          {error}
        </div>
      )}
      {busy && (
        <div className="fixed inset-0 z-[10001] bg-black/30 flex items-center justify-center">
          <div className="text-sm" style={{ color: NEON }}>Submitting…</div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// REGULARISATION MODAL
// ─────────────────────────────────────────────────────────────
export function ChatRegularisationModal({ open, onClose, onSuccess, date }) {
  const [inTime,  setInTime]  = useState("");
  const [outTime, setOutTime] = useState("");
  const [reason,  setReason]  = useState("Forgot to swipe");
  const [file,    setFile]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("date",     date || new Date().toISOString().split("T")[0]);
      fd.append("req_in",  inTime);
      fd.append("req_out", outTime);
      fd.append("reason",   reason);
      if (file) fd.append("file", file);

      const res = await fetch(`${API_BASE}/api/attendance/regularise`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body:    fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Submission failed");
      }

      onSuccess?.(
        `✅ Attendance regularisation for ${date || "today"} has been submitted. Your manager will review it shortly.`
      );
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  // Pass a patched onClose that runs our submit
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]">
      <div
        className="w-full max-w-3xl rounded-2xl p-6 bg-black border"
        style={{ borderColor: "rgba(0,255,153,0.35)" }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>
            Attendance Regularisation{date ? ` – ${new Date(date).toLocaleDateString()}` : ""}
          </h2>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>

        <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-xs mb-1 text-white/50">Date</label>
            <input
              type="date"
              defaultValue={date}
              className="w-full h-10 rounded-md px-3 bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(0,255,153,0.30)" }}
              readOnly
            />
          </div>
          <div>
            <label className="block text-xs mb-1 text-white/50">In Time</label>
            <input
              type="time"
              value={inTime}
              onChange={(e) => setInTime(e.target.value)}
              required
              className="w-full h-10 rounded-md px-3 bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(0,255,153,0.30)" }}
            />
          </div>
          <div>
            <label className="block text-xs mb-1 text-white/50">Out Time</label>
            <input
              type="time"
              value={outTime}
              onChange={(e) => setOutTime(e.target.value)}
              className="w-full h-10 rounded-md px-3 bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(0,255,153,0.30)" }}
            />
          </div>
          <div>
            <label className="block text-xs mb-1 text-white/50">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-10 rounded-md px-3 bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(0,255,153,0.30)" }}
            >
              <option>Forgot to swipe</option>
              <option>Work From Home</option>
              <option>Client Visit</option>
              <option>Sick</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs mb-1 text-white/50">Supporting Document (optional)</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full h-10 rounded-md px-3 bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(0,255,153,0.30)" }}
            />
          </div>

          {error && (
            <p className="md:col-span-2 text-red-400 text-sm">{error}</p>
          )}

          <div className="md:col-span-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-md border text-sm"
              style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-4 rounded-md border text-sm"
              style={{
                color: NEON,
                backgroundColor: "rgba(0,255,153,0.15)",
                borderColor: "rgba(0,255,153,0.35)",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TICKET MODAL
// ─────────────────────────────────────────────────────────────
export function ChatTicketModal({ open, onClose, onSuccess }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]">
      <div className="w-full max-w-lg">
        <div className="flex justify-end mb-2">
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>
        <TicketSubmissionForm
          onSubmitSuccess={(data) => {
            onSuccess?.(`✅ Ticket submitted successfully. Ticket ID: ${data?.id || data?.ticket_id || "—"}. You'll receive updates as it's processed.`);
            onClose?.();
          }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PAYSLIP CORRECTION MODAL
// ─────────────────────────────────────────────────────────────
export function ChatCorrectionModal({ open, onClose, onSuccess }) {
  const [month,       setMonth]       = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [message,     setMessage]     = useState("");
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState("");

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/payroll/correction`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ month, message }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Submission failed");
      }

      onSuccess?.("✅ Payslip correction request submitted. The payroll team will review and respond within 2 business days.");
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div
        className="w-full max-w-md rounded-2xl p-6 bg-black border"
        style={{ borderColor: "rgba(0,255,153,0.35)" }}
      >
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>
            Request Payslip Correction
          </h2>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>
        <label className="block text-xs text-white/50 mb-1">Payslip Month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />
        <label className="block text-xs text-white/50 mb-1">Issue Description</label>
        <textarea
          placeholder="Describe the issue clearly..."
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-4"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />
        {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="h-10 px-4 rounded-md border text-sm"
            style={{
              color: NEON,
              backgroundColor: "rgba(0,255,153,0.15)",
              borderColor: "rgba(0,255,153,0.35)",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SELF REVIEW MODAL
// ─────────────────────────────────────────────────────────────
export function ChatSelfReviewModal({ open, onClose, onSuccess }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]">
      <div className="w-full max-w-lg">
        <div className="flex justify-end mb-2">
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>
        {/* SelfReviewForm is self-contained — wrap to intercept success */}
        <SelfReviewFormWrapped
          onSuccess={() => {
            onSuccess?.("✅ Self review submitted successfully. Your manager will be notified.");
            onClose?.();
          }}
        />
      </div>
    </div>
  );
}

// Wrap SelfReviewForm to intercept the status message
function SelfReviewFormWrapped({ onSuccess }) {
  const [form, setForm]     = useState({ achievements: "", challenges: "", goals_next: "" });
  const [status, setStatus] = useState("");
  const [busy, setBusy]     = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/self-review`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      onSuccess?.();
    } catch (err) {
      setStatus("Error submitting review. Please try again.");
      setBusy(false);
    }
  };

  return (
    <section
      className="p-4 rounded-2xl border bg-black/60"
      style={{ borderColor: "rgba(0,255,153,0.20)" }}
    >
      <h2 className="text-xl font-semibold mb-4" style={{ color: NEON }}>Self Review</h2>
      <form onSubmit={handleSubmit} className="space-y-3 text-sm">
        <textarea name="achievements" placeholder="Your key achievements"
          value={form.achievements} onChange={handleChange}
          className="w-full rounded-md p-2 bg-black/40 border text-white" rows={3}
          style={{ borderColor: "rgba(255,255,255,0.15)" }} />
        <textarea name="challenges" placeholder="Challenges faced"
          value={form.challenges} onChange={handleChange}
          className="w-full rounded-md p-2 bg-black/40 border text-white" rows={3}
          style={{ borderColor: "rgba(255,255,255,0.15)" }} />
        <textarea name="goals_next" placeholder="Goals for next period"
          value={form.goals_next} onChange={handleChange}
          className="w-full rounded-md p-2 bg-black/40 border text-white" rows={3}
          style={{ borderColor: "rgba(255,255,255,0.15)" }} />
        {status && <p className="text-red-400 text-sm">{status}</p>}
        <button type="submit" disabled={busy}
          className="h-10 px-4 rounded-md border text-sm font-medium"
          style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.15)", borderColor: "rgba(0,255,153,0.35)", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Submitting…" : "Submit Review"}
        </button>
      </form>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────
// INCOME CERTIFICATE REQUEST MODAL
// ─────────────────────────────────────────────────────────────
export function ChatIncomeRequestModal({ open, onClose, onSuccess }) {
  const [purpose, setPurpose] = useState("Visa");
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/payroll/income-certificate`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ purpose }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Request failed");
      }
      onSuccess?.("✅ Income certificate request submitted. HR will process and send it to your registered email within 3 business days.");
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="w-full max-w-md rounded-2xl p-6 bg-black border" style={{ borderColor: "rgba(0,255,153,0.35)" }}>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>Request Income Certificate</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}>✖</button>
        </div>
        <p className="text-white/70 text-sm mb-3">
          This will send a request to HR for issuing your income certificate.
        </p>
        <label className="block text-xs text-white/50 mb-1">Purpose</label>
        <select
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-4"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        >
          {["Visa", "Bank Loan", "Rental", "Other"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={busy} className="h-10 px-4 rounded-md border text-sm"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.15)", borderColor: "rgba(0,255,153,0.35)", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SALARY BREAKDOWN MODAL (read-only, fetches own payslip data)
// ─────────────────────────────────────────────────────────────
export function ChatSalaryBreakdownModal({ open, onClose, payslip: propPayslip }) {
  const [payslip, setPayslip] = useState(propPayslip || null);
  const [loading, setLoading] = useState(false);

  // Fetch latest payslip if not passed in as prop
  useEffect(() => {
    if (!open || propPayslip) return;
    setLoading(true);
    fetch(`${API_BASE}/api/payroll/latest-payslip`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => setPayslip(d?.data || d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, propPayslip]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="w-full max-w-3xl rounded-2xl p-6 bg-black border text-white" style={{ borderColor: "rgba(0,255,153,0.35)" }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>
            Salary Breakdown{payslip?.month ? ` • ${payslip.month}` : ""}
          </h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}>✖</button>
        </div>

        {loading && <p className="text-white/50 text-sm">Loading payslip…</p>}
        {!loading && payslip && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SBCard title="Earnings"><KVTable data={payslip.earnings} /></SBCard>
              <SBCard title="Deductions"><KVTable data={payslip.deductions} /></SBCard>
            </div>
            <div className="flex justify-between font-semibold mt-5 border-t border-white/10 pt-3">
              <span>Gross Pay: ₹{payslip.grossPay}</span>
              <span>Net Pay: ₹{payslip.netPay}</span>
            </div>
          </>
        )}
        {!loading && !payslip && (
          <p className="text-white/50 text-sm">No payslip data available.</p>
        )}

        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function SBCard({ title, children }) {
  return (
    <div className="rounded-xl p-4 border bg-black/40" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
      <h3 className="font-semibold mb-2" style={{ color: NEON }}>{title}</h3>
      {children}
    </div>
  );
}
function KVTable({ data = {} }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {Object.entries(data).map(([k, v]) =>
          v ? (
            <tr key={k} className="border-b border-white/10">
              <td className="py-1 pr-3">{k}</td>
              <td className="py-1">₹{v}</td>
            </tr>
          ) : null
        )}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────
// ADMIN TICKET DRAWER — manage a specific ticket
// Fetches ticket by ID if not passed directly
// ─────────────────────────────────────────────────────────────
export function ChatTicketDrawer({ open, onClose, onSuccess, ticketId, ticket: propTicket }) {
  const [ticket,   setTicket]   = useState(propTicket || null);
  const [loading,  setLoading]  = useState(false);
  const [assignee, setAssignee] = useState("");
  const [status,   setStatus]   = useState("Open");
  const [priority, setPriority] = useState("P3");
  const [note,     setNote]     = useState("");
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState("");

  useEffect(() => {
    if (!open) return;
    if (propTicket) { setTicket(propTicket); setAssignee(propTicket.assignee || ""); setStatus(propTicket.status || "Open"); setPriority(propTicket.priority || "P3"); return; }
    if (!ticketId)  return;
    setLoading(true);
    fetch(`${API_BASE}/api/tickets/${ticketId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => { const t = d?.data || d; setTicket(t); setAssignee(t.assignee || ""); setStatus(t.status || "Open"); setPriority(t.priority || "P3"); })
      .catch(() => setError("Could not load ticket."))
      .finally(() => setLoading(false));
  }, [open, ticketId, propTicket]);

  const handleSave = async () => {
    if (!ticket) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/tickets/${ticket.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ assignee, status, priority, note: note || undefined }),
      });
      if (!res.ok) throw new Error("Update failed");
      onSuccess?.(`✅ Ticket ${ticket.id} updated — Status: ${status}, Priority: ${priority}, Assigned to: ${assignee}.`);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10070]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-black border-l border-white/10 p-5 overflow-auto text-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: NEON }}>{ticket?.subject || "Ticket"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        {loading && <p className="text-white/50 text-sm">Loading…</p>}
        {ticket && !loading && (
          <>
            <div className="space-y-1 text-sm mb-4">
              {[["Ticket", ticket.id], ["Category", ticket.category], ["Requester", ticket.requester], ["Dept", ticket.dept]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-white/60">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="h-px bg-white/10 my-4" />
            <div className="grid grid-cols-3 gap-3 text-sm mb-4">
              {[["Assignee", assignee, setAssignee, ["HR Payroll","HR Ops","IT Help","IT L2"]],
                ["Status",   status,   setStatus,   ["Open","Pending","Escalated","Closed"]],
                ["Priority", priority, setPriority, ["P1","P2","P3","P4"]]].map(([label, val, setter, opts]) => (
                <label key={label}>{label}
                  <select value={val} onChange={(e) => setter(e.target.value)}
                    className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white">
                    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <div className="mb-4">
              <p className="text-sm font-medium mb-1">Add note</p>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full h-20 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm" />
            </div>
          </>
        )}

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={busy || !ticket}
            className="px-4 py-2 rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 text-sm"
            style={{ opacity: busy ? 0.6 : 1 }}>
            {busy ? "Saving…" : "Save changes"}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 text-sm text-white/60 hover:text-white">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LEAVE APPROVAL DRAWER — manager/admin approve or reject
// ─────────────────────────────────────────────────────────────
export function ChatLeaveApprovalDrawer({ open, onClose, onSuccess, requestId, request: propRequest }) {
  const [request, setRequest] = useState(propRequest || null);
  const [loading, setLoading] = useState(false);
  const [note,    setNote]    = useState("");
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!open) return;
    if (propRequest) { setRequest(propRequest); return; }
    if (!requestId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/leave/${requestId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => setRequest(d?.data || d))
      .catch(() => setError("Could not load leave request."))
      .finally(() => setLoading(false));
  }, [open, requestId, propRequest]);

  const action = async (decision) => {
    if (!request) return;
    setBusy(true); setError("");
    try {
      // Backend exposes /approve and /reject — there is no /decision route.
      // "override" routes through /approve too (managers can force-approve a
      // pending or rejected request).
      const path = decision === "reject" ? "reject" : "approve";
      const res = await fetch(`${API_BASE}/api/leave/${request.id}/${path}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ comment: note || null }),
      });
      if (!res.ok) throw new Error("Action failed");
      const verb = decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "overridden";
      onSuccess?.(`✅ Leave request for ${request.employeeName || "employee"} has been ${verb}.`);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10080]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-black border-l border-white/10 p-5 overflow-auto text-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: NEON }}>
            {request ? `${request.id} — ${request.employeeName}` : "Leave Request"}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        {loading && <p className="text-white/50 text-sm">Loading…</p>}
        {request && !loading && (
          <div className="space-y-2 text-sm mb-4">
            {[["Type", request.type], ["Dates", `${request.startDate} → ${request.endDate} (${request.days} day(s))`],
              ["Department", request.dept], ["Status", request.status]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-white/80">
                <span className="text-white/60">{k}</span><span>{v}</span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4">
          <p className="text-sm mb-1 text-white/70">Note (optional)</p>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note"
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white mb-3" />
          {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => action("approve")} disabled={busy || !request}
              className="px-4 py-2 rounded-xl border text-sm"
              style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}>
              Approve
            </button>
            <button onClick={() => action("reject")} disabled={busy || !request}
              className="px-4 py-2 rounded-xl border border-white/10 bg-white/10 text-sm hover:bg-white/20">
              Reject
            </button>
            <button onClick={() => action("override")} disabled={busy || !request}
              className="px-4 py-2 rounded-xl border text-sm"
              style={{ backgroundColor: "rgba(239,68,68,0.15)", borderColor: "rgba(239,68,68,0.35)" }}>
              Override
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EMPLOYEE PAY DRAWER — admin update salary / adjustments
// ─────────────────────────────────────────────────────────────
export function ChatEmployeePayDrawer({ open, onClose, onSuccess, employeeId, row: propRow }) {
  const [row,     setRow]     = useState(propRow || null);
  const [loading, setLoading] = useState(false);
  const [ctc,     setCtc]     = useState(0);
  const [note,    setNote]    = useState("");
  const [eff,     setEff]     = useState(new Date().toISOString().slice(0, 10));
  const [adjType, setAdjType] = useState("EARNING");
  const [adjAmt,  setAdjAmt]  = useState(0);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!open) return;
    if (propRow) { setRow(propRow); setCtc(propRow.currentAnnualSalary || 0); return; }
    if (!employeeId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/admin/employees/${employeeId}/payroll`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => { const r = d?.data || d; setRow(r); setCtc(r.currentAnnualSalary || 0); })
      .catch(() => setError("Could not load employee pay data."))
      .finally(() => setLoading(false));
  }, [open, employeeId, propRow]);

  const saveCTC = async () => {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/employees/${row.id}/salary`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ annual_salary: ctc, effective_date: eff, note }),
      });
      if (!res.ok) throw new Error("Save failed");
      onSuccess?.(`✅ Annual salary for ${row.name} updated to ₹${Number(ctc).toLocaleString("en-IN")} effective ${eff}.`);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const addAdjustment = async () => {
    if (!adjAmt) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/employees/${row.id}/adjustment`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ type: adjType, amount: Number(adjAmt), one_time: true }),
      });
      if (!res.ok) throw new Error("Adjustment failed");
      onSuccess?.(`✅ One-time ${adjType.toLowerCase()} of ₹${adjAmt} added for ${row.name} for this payroll run.`);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10070]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-lg bg-black border-l border-white/10 p-5 overflow-auto text-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: NEON }}>{row ? `${row.name} — ${row.empCode}` : "Employee Pay"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        {loading && <p className="text-white/50 text-sm">Loading…</p>}
        {row && !loading && (
          <>
            <div className="space-y-2 text-sm mb-4">
              {[["Department", row.dept], ["Current CTC", `₹${Number(row.currentAnnualSalary).toLocaleString("en-IN")}`]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-white/60">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="h-px bg-white/10 my-4" />
            <p className="font-medium text-sm mb-3">Update Annual Salary</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="text-sm">New CTC (₹)
                <input type="number" value={ctc} onChange={(e) => setCtc(Number(e.target.value || 0))}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white" />
              </label>
              <label className="text-sm">Effective date
                <input type="date" value={eff} onChange={(e) => setEff(e.target.value)}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white" />
              </label>
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm mb-3" />
            <button onClick={saveCTC} disabled={busy}
              className="px-4 py-2 rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 text-sm mb-6">Save CTC</button>

            <div className="h-px bg-white/10 my-4" />
            <p className="font-medium text-sm mb-3">One-time Adjustment</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <label className="text-sm">Type
                <select value={adjType} onChange={(e) => setAdjType(e.target.value)}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white">
                  <option value="EARNING">Earning</option>
                  <option value="DEDUCTION">Deduction</option>
                </select>
              </label>
              <label className="text-sm col-span-2">Amount (₹)
                <input type="number" value={adjAmt} onChange={(e) => setAdjAmt(Number(e.target.value || 0))}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white" />
              </label>
            </div>
            <button onClick={addAdjustment} disabled={busy}
              className="px-4 py-2 rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 text-sm">Add Adjustment</button>
          </>
        )}
        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PERFORMANCE REVIEW DRAWER — manager/admin set rating + stage
// ─────────────────────────────────────────────────────────────
export function ChatReviewDrawer({ open, onClose, onSuccess, employeeId, row: propRow }) {
  const [row,       setRow]       = useState(propRow || null);
  const [loading,   setLoading]   = useState(false);
  const [rating,    setRating]    = useState(propRow?.rating || "");
  const [nextStage, setNextStage] = useState("Finalize");
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState("");

  useEffect(() => {
    if (!open) return;
    if (propRow) { setRow(propRow); setRating(propRow.rating || ""); return; }
    if (!employeeId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/performance/employee/${employeeId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => { const r = d?.data || d; setRow(r); setRating(r.rating || ""); })
      .catch(() => setError("Could not load performance data."))
      .finally(() => setLoading(false));
  }, [open, employeeId, propRow]);

  const saveRating = async () => {
    if (!row || !rating) return;
    setBusy(true); setError("");
    try {
      // Backend expects user_id (not the team-record id) — the new
      // /api/performance/{user_id}/rating endpoint resolves the row by user.
      const targetUserId = employeeId || row?.user_id;
      const res = await fetch(`${API_BASE}/api/performance/${targetUserId}/rating`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ rating: Number(rating) }),
      });
      if (!res.ok) throw new Error("Save failed");
      onSuccess?.(`✅ Performance rating for ${row.name} set to ${rating}/5.`);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const moveStage = async () => {
    if (!row) return;
    setBusy(true); setError("");
    try {
      const targetUserId = employeeId || row?.user_id;
      const res = await fetch(`${API_BASE}/api/performance/${targetUserId}/stage`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ stage: nextStage }),
      });
      if (!res.ok) throw new Error("Stage update failed");
      onSuccess?.(`✅ Performance review for ${row.name} moved to "${nextStage}" stage.`);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10070]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-lg bg-black border-l border-white/10 p-5 overflow-auto text-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold" style={{ color: NEON }}>{row ? `${row.name} — ${row.empCode}` : "Performance Review"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        {loading && <p className="text-white/50 text-sm">Loading…</p>}
        {row && !loading && (
          <>
            <div className="space-y-2 text-sm mb-4">
              {[["Department", row.dept], ["Manager", row.manager], ["Stage", row.stage]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-white/60">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="h-px bg-white/10 my-4" />
            <p className="font-medium text-sm mb-2">Set Rating (1–5)</p>
            <div className="flex items-center gap-3 mb-4">
              <input type="number" step="0.1" min="1" max="5" value={rating}
                onChange={(e) => setRating(e.target.value)}
                className="w-24 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white" />
              <button onClick={saveRating} disabled={busy}
                className="px-4 py-2 rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 text-sm">Save Rating</button>
            </div>
            <div className="h-px bg-white/10 my-4" />
            <p className="font-medium text-sm mb-2">Move Stage</p>
            <div className="flex items-center gap-3">
              <select value={nextStage} onChange={(e) => setNextStage(e.target.value)}
                className="w-48 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white">
                {["Self","Mgr Review","Peer","Calibration","Finalize"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={moveStage} disabled={busy}
                className="px-4 py-2 rounded-xl border border-white/10 bg-white/10 hover:bg-white/20 text-sm">Move</button>
            </div>
          </>
        )}
        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREATE USER FORM — admin only, wrapped in a modal
// ─────────────────────────────────────────────────────────────
const CREATE_USER_INITIAL = {
  employee_id: "", first_name: "", last_name: "", email: "",
  title: "", department: "", location: "", employment_type: "Full-time",
  manager_email: "", start_date: new Date().toISOString().slice(0, 10), roles: ["staff"],
};

export function ChatCreateUserModal({ open, onClose, onSuccess }) {
  const [form,   setForm]   = useState(CREATE_USER_INITIAL);
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleRole = (role) =>
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role],
    }));

  const validate = () => {
    if (!form.employee_id.trim()) return "Employee ID is required.";
    if (!form.first_name.trim())  return "First name is required.";
    if (!form.last_name.trim())   return "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Valid work email is required.";
    if (!form.title.trim())       return "Title is required.";
    if (!form.department.trim())  return "Department is required.";
    if (!form.location.trim())    return "Location is required.";
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Failed to create user.");
      }
      onSuccess?.(`✅ User ${form.first_name} ${form.last_name} (${form.email}) created successfully with role: ${form.roles.join(", ")}.`);
      onClose?.();
      setForm(CREATE_USER_INITIAL);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4">
      <div className="w-full max-w-2xl rounded-2xl p-6 bg-black border overflow-y-auto max-h-[90vh]" style={{ borderColor: "rgba(0,255,153,0.35)" }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>Create New User</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}>✖</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-4">
          {[["Employee ID","employee_id","text"],["First name","first_name","text"],["Last name","last_name","text"],
            ["Work email","email","email"],["Title","title","text"],["Department","department","text"],
            ["Location","location","text"],["Manager (email)","manager_email","text"],["Start date","start_date","date"]].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-xs text-white/60">{label}</label>
              <input type={type} value={form[key]} onChange={(e) => set(key, e.target.value)}
                className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white" />
            </div>
          ))}
          <div>
            <label className="text-xs text-white/60">Employment type</label>
            <select value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)}
              className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white">
              {["Full-time","Contract","Intern"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-white/60">Roles</label>
            <div className="mt-1 flex gap-2">
              {["staff","manager","admin"].map((r) => (
                <button key={r} type="button" onClick={() => toggleRole(r)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${form.roles.includes(r) ? "bg-white/10 border-white/30" : "bg-white/0 border-white/10 hover:bg-white/5"}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}>Cancel</button>
          <button onClick={submit} disabled={busy} className="h-10 px-4 rounded-md border text-sm"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.15)", borderColor: "rgba(0,255,153,0.35)", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Creating…" : "Create User"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MEMBER ATTENDANCE DRAWER — manager view of team member
// ─────────────────────────────────────────────────────────────
export function ChatMemberAttendanceDrawer({ open, onClose, onSuccess, member: propMember, employeeId }) {
  const [member,  setMember]  = useState(propMember || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (propMember) { setMember(propMember); return; }
    if (!employeeId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/manager/team/${employeeId}/attendance`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => setMember(d?.data || d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, employeeId, propMember]);

  const approveRegularisation = async () => {
    if (!member) return;
    try {
      await fetch(`${API_BASE}/api/attendance/regularise/${member.pendingRegId}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      onSuccess?.(`✅ Attendance regularisation for ${member.name} approved.`);
      onClose?.();
    } catch {
      onSuccess?.("Could not approve regularisation. Please try from the Attendance module.");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white/5 border-l border-white/10 backdrop-blur-md text-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Attendance • {member?.name || "Member"}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white">✕</button>
        </div>

        {loading && <p className="text-white/50 text-sm mt-4">Loading…</p>}
        {member && !loading && (
          <div className="mt-4 space-y-4 text-sm">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-white/60 text-xs mb-1">Recent Punches</p>
              <ul className="list-disc list-inside text-white/80">
                {(member.recentPunches || [{ time: "09:12", type: "IN" }, { time: "17:48", type: "OUT" }]).map((p, i) => (
                  <li key={i}>{p.time} {p.type}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-white/60 text-xs mb-1">Quick Actions</p>
              <div className="flex gap-2">
                <button onClick={approveRegularisation}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/10 hover:bg-white/20">
                  Approve Regularisation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MEMBER LEAVE DRAWER — manager view of team member's leave
// ─────────────────────────────────────────────────────────────
export function ChatMemberLeaveDrawer({ open, onClose, onSuccess, member: propMember, employeeId }) {
  const [member,  setMember]  = useState(propMember || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (propMember) { setMember(propMember); return; }
    if (!employeeId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/manager/team/${employeeId}/leave`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => setMember(d?.data || d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, employeeId, propMember]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white/5 border-l border-white/10 backdrop-blur-md text-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Leave • {member?.name || "Member"}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white">✕</button>
        </div>

        {loading && <p className="text-white/50 text-sm mt-4">Loading…</p>}
        {member && !loading && (
          <div className="mt-4 space-y-4 text-sm">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-white/60 text-xs mb-1">Balances</p>
              <div className="grid grid-cols-3 gap-2">
                {[["Annual", member.leaveBalance?.annual ?? 8], ["Sick", member.leaveBalance?.sick ?? 6], ["Casual", member.leaveBalance?.casual ?? 2]].map(([label, val]) => (
                  <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-2 text-center">
                    <p className="text-xs text-white/60">{label}</p>
                    <p className="font-medium">{val}</p>
                  </div>
                ))}
              </div>
            </div>
            {(member.recentRequests || []).length > 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-white/60 text-xs mb-1">Recent Requests</p>
                <ul className="list-disc list-inside text-white/80">
                  {member.recentRequests.map((r, i) => (
                    <li key={i}>{r.type} • {r.dates} • {r.status}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TASK CREATE MODAL
// ─────────────────────────────────────────────────────────────
export function ChatTaskCreateModal({ open, onClose, onSuccess }) {
  const [title,       setTitle]       = useState("");
  const [description, setDescription] = useState("");
  const [projectId,   setProjectId]   = useState("");
  const [priority,    setPriority]    = useState("Medium");
  const [dueDate,     setDueDate]     = useState("");
  const [projects,    setProjects]    = useState([]);
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState("");

  // Fetch projects the user is a member of so they can pick one.
  useEffect(() => {
    if (!open) return;
    fetch(`${API_BASE}/api/projects/my`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setProjects(Array.isArray(d) ? d : (d?.data || [])))
      .catch(() => setProjects([]));
  }, [open]);

  const closedRef = useRef(false);
  useEffect(() => {
    closedRef.current = false;
    return () => {
      if (!closedRef.current) onClose?.();
    };
  }, []);

  const handleSubmit = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    setBusy(true); setError("");
    try {
      const body = {
        title:       title.trim(),
        description: description.trim() || null,
        project_id:  projectId || null,
        priority,
        due_date:    dueDate || null,
      };
      const res = await fetch(`${API_BASE}/api/projects/tasks`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Submission failed");
      }
      onSuccess?.("✅ Task created successfully");
      closedRef.current = true;
      // onClose not called here — onSuccess (confirmAction) handles dismissal
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]"
      onClick={() => { closedRef.current = true; onClose?.(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 bg-black border"
        style={{ borderColor: "rgba(0,255,153,0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>
            Create Task
          </h2>
          <button
            onClick={() => { closedRef.current = true; onClose?.(); }}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>

        <label className="block text-xs text-white/50 mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs doing?"
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <label className="block text-xs text-white/50 mb-1">Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Add detail if helpful…"
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <label className="block text-xs text-white/50 mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        >
          <option value="">— No project —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <label className="block text-xs text-white/50 mb-1">Priority</label>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        >
          {["Critical", "High", "Medium", "Low"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <label className="block text-xs text-white/50 mb-1">Due date (optional)</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-4"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => { closedRef.current = true; onClose?.(); }}
            className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="h-10 px-4 rounded-md border text-sm"
            style={{
              color: NEON,
              backgroundColor: "rgba(0,255,153,0.15)",
              borderColor: "rgba(0,255,153,0.35)",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Creating…" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROJECT CREATE MODAL
// Manager / admin / superadmin only — staff and executive
// see nothing.
// ─────────────────────────────────────────────────────────────
export function ChatProjectCreateModal({ open, onClose, onSuccess }) {
  // Self-gate by role — read from the cached user object.
  let role = "";
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    role = String(u?.role || "").toLowerCase();
  } catch { /* fall through to disallowed */ }
  const allowed = role === "manager" || role === "admin" || role === "superadmin";

  const [name,        setName]        = useState("");
  const [description, setDescription] = useState("");
  const [startDate,   setStartDate]   = useState("");
  const [endDate,     setEndDate]     = useState("");
  const [status,      setStatus]      = useState("Planning");
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState("");

  const closedRef = useRef(false);
  useEffect(() => {
    closedRef.current = false;
    return () => {
      if (!closedRef.current) onClose?.();
    };
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Name is required."); return; }
    setBusy(true); setError("");
    try {
      const body = {
        name:        name.trim(),
        description: description.trim() || null,
        start_date:  startDate || null,
        end_date:    endDate   || null,
        status,
      };
      const res = await fetch(`${API_BASE}/api/manager/projects`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Submission failed");
      }
      onSuccess?.("✅ Project created successfully");
      closedRef.current = true;
      // onClose not called here — onSuccess (confirmAction) handles dismissal
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  if (!allowed) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]"
      onClick={() => { closedRef.current = true; onClose?.(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 bg-black border"
        style={{ borderColor: "rgba(0,255,153,0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>
            Create Project
          </h2>
          <button
            onClick={() => { closedRef.current = true; onClose?.(); }}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>

        <label className="block text-xs text-white/50 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Project name"
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <label className="block text-xs text-white/50 mb-1">Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="What's this project about?"
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs text-white/50 mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(255,255,255,0.25)" }}
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(255,255,255,0.25)" }}
            />
          </div>
        </div>

        <label className="block text-xs text-white/50 mb-1">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-4"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        >
          {["Planning", "Active"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => { closedRef.current = true; onClose?.(); }}
            className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="h-10 px-4 rounded-md border text-sm"
            style={{
              color: NEON,
              backgroundColor: "rgba(0,255,153,0.15)",
              borderColor: "rgba(0,255,153,0.35)",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}
